import { cooperate } from '@tol/core/work';
// A GTFS city's live data in the browser, wherever it's drawn: its network
// (network.json, geometry.json), its trains (/trains) polled into the page's
// store under one key, and its alerts (/alerts). The city's own page
// (page.ts) and the world map (apps/world's gtfs-region.ts) each make one and
// say only what differs: where its files are, the ids its trains take in the
// store, and what the page shows.

import * as Core from '@tol/core/core';
import { prepareComputedSteps } from '@tol/core/feed';
import type { ApiClient, SampledJSON } from '@tol/core/feed';
import type { Arrival, ComputedTrains, GeometryFile, LineId, Network, TrainStore } from '@tol/core/types';
import { closuresOf } from './closures.ts';
import { unroutedGps } from './unrouted-gps.ts';
import { clearRegion, createPollHintDelay, loadJSON, loadOptionalJSON, pollLoop } from './poll.ts';
import type { OptionalJSON } from './poll.ts';
import {
  computeTrainsSteps,
  createGpsMemory,
  feedsState,
  gtfsNetworkAsync,
  isNetworkFile,
  isTrains,
} from './trains.ts';
import { isPartitionManifest, mergeGeometryParts, mergeNetworkParts, selectedPartitions } from './partitions.ts';
import type { GtfsPartitionBox, GtfsPartitionManifest } from './partitions.ts';
import type { GtfsAlerts, GtfsGeometryFile, GtfsNetworkFile, GtfsTrains, GtfsTrip, LineAlert } from './types.ts';

// How often the alerts are asked for.
const ALERTS_MS = 60_000;
// A whole-country selection of many small parts is the combined file with
// extra requests. City views still use the selected pieces.
const COMBINED_PARTS_FROM = 32;

type PartitionSelection = {
  readonly built: string;
  readonly ids: readonly string[];
  readonly key: string;
  readonly all: boolean;
};

function partitionSelection(
  manifest: GtfsPartitionManifest,
  bounds: GtfsPartitionBox,
  includeMinor: boolean,
): PartitionSelection {
  const ids = selectedPartitions(manifest, bounds, includeMinor);
  return {
    built: manifest.built,
    ids,
    key: `${manifest.built}:${includeMinor ? 'all' : 'major'}:${ids.join(',')}`,
    all: manifest.parts.length >= COMBINED_PARTS_FROM && ids.length === manifest.parts.length,
  };
}

export interface GtfsClientOptions {
  // In its warnings ("nyc: train refresh failed").
  readonly name: string;
  // Where its server is: `${base}network.json`, `${base}trains`. version: the
  // page's deploy stamp (?v=) on its static files; '' for another server's,
  // which stamps its own deploys and would refuse another's.
  readonly base: string;
  readonly version: string;
  readonly api: Pick<ApiClient, 'getJSON' | 'getSampledJSON'>;
  // Its trains go in the store under `key`, replacing `lines` (store ids).
  readonly store: TrainStore;
  readonly key: number;
  readonly lines: readonly LineId[];
  // Its computed trains in the store's ids (the world map's prefix); by
  // default as computed.
  readonly storeIds?: (computed: ComputedTrains) => ComputedTrains;
  readonly now: () => number;
  readonly pollMs: number;
  // It serves /alerts.
  readonly alerts: boolean;
  // load() gives it without its minor tier (network-major.json, major.ts)
  // and full() the whole of it.
  readonly major: boolean;
  // The world map may instead choose generation-bound network pieces for its
  // current bounds. A city's own page continues to use the combined files.
  readonly partitioned?: boolean;
  readonly isGeometry: (v: unknown) => v is GeometryFile;
  // After its trains, alerts or loading changed.
  readonly onUpdate: () => void;
  // After each train poll: the feeds with no live data (none: empty), or
  // null when the poll failed.
  readonly onProblem: (down: readonly string[] | null, feeds?: GtfsTrains['feeds']) => void;
  // Its network changed without load() or full() answering: geometry that
  // failed at first came in, or the server rebuilt its timetable.
  readonly onNetwork: (net: Network) => void;
  // Prepare the world's matching network, returning its synchronous publish
  // step. Commit both together before trains can use newly added stations.
  readonly prepareNetwork?: (net: Network, signal?: AbortSignal) => Promise<() => void>;
  // A city page reloads for a rebuilt timetable (its trains' networkBuilt)
  // instead of replacing the network.
  readonly onRebuilt?: () => void;
}

export interface GtfsClient {
  // Its network in its own ids: fetched once, later calls share it; a
  // failed fetch is tried again by the next call.
  readonly load: (bounds?: GtfsPartitionBox) => Promise<Network>;
  // For a load() without the minor tier: the whole network, fetched once;
  // null when there's nothing more. A partitioned world region reconciles
  // the pieces for `bounds` instead.
  readonly full: (bounds?: GtfsPartitionBox) => Promise<Network> | null;
  // Updates the relevance of a partition selection. True means a completed
  // selection no longer matches this view and tier and should be reconciled.
  readonly view: (bounds: GtfsPartitionBox, includeMinor: boolean) => boolean;
  readonly network: () => Network | null;
  readonly file: () => GtfsNetworkFile | null;
  // Fetches and drops one train answer while a map flight is in progress.
  readonly prefetch: (bounds: GtfsPartitionBox, includeMinor: boolean) => void;
  // Polls its trains (and alerts) now and on schedule; the promise settles
  // with the first train poll. Idempotent.
  readonly start: () => Promise<void>;
  // Stops polling, cancelling what's in flight; what it has stays.
  readonly stop: () => void;
  // Forgets its trains (the store's) and alerts.
  readonly clear: () => void;
  // Evict an inactive city's decoded files. A later load starts fresh.
  readonly release: () => void;
  readonly polling: () => boolean;
  // Polling, and its first trains aren't all in yet.
  readonly loading: () => boolean;
  // The card's trip, by the city's trip id; a line's alert and whether the
  // alerts speak for it, by the city's line id.
  readonly trip: (id: string) => GtfsTrip | undefined;
  readonly alert: (line: LineId) => LineAlert | undefined;
  readonly covered: (line: LineId) => boolean;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const errorStatus = (error: unknown): number =>
  typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number'
    ? error.status
    : 0;
const partKey = (built: string, id: string): string => `${built}|${id}`;
const partId = (key: string): string => key.slice(key.indexOf('|') + 1);

// An alert lineStatus can show: the fields it reads, of the right types.
function isLineAlert(v: unknown): v is LineAlert {
  return (
    isObject(v) &&
    typeof v['type'] === 'string' &&
    typeof v['text'] === 'string' &&
    typeof v['planned'] === 'boolean' &&
    typeof v['count'] === 'number' &&
    (v['detail'] === undefined || typeof v['detail'] === 'string') &&
    (v['all'] === undefined ||
      (Array.isArray(v['all']) &&
        v['all'].every(
          (i) =>
            isObject(i) &&
            typeof i['type'] === 'string' &&
            typeof i['text'] === 'string' &&
            (i['detail'] === undefined || typeof i['detail'] === 'string'),
        )))
  );
}

function isAlerts(v: unknown): v is GtfsAlerts {
  return (
    isObject(v) &&
    isObject(v['lines']) &&
    !Array.isArray(v['lines']) &&
    Object.values(v['lines']).every((a) => isLineAlert(a)) &&
    (v['covered'] === undefined || (Array.isArray(v['covered']) && v['covered'].every((x) => typeof x === 'string')))
  );
}

export function createGtfsClient(options: GtfsClientOptions): GtfsClient {
  const { name, base, version, api, store, key, lines, now } = options;
  const knownLines = new Set(lines);
  const storeIds = options.storeIds ?? ((c: ComputedTrains): ComputedTrains => c);
  let file: GtfsNetworkFile | null = null;
  let local: Network | null = null;
  type NetworkPublication = {
    readonly file: GtfsNetworkFile;
    readonly net: Network;
    readonly publish: (() => void) | undefined;
  };
  let pendingNetwork: NetworkPublication | null = null;
  let loading: Promise<Network> | null = null;
  let geometryLoad: OptionalJSON<GeometryFile> | null = null;
  // Each network fetch's; a superseded fetch's files are dropped.
  let networkGeneration = 0;
  // stop() ends the current partition session without evicting settled files.
  // A later camera visit starts a new session.
  let partitionSession = 0;
  let lifetime = 0;
  // The last networkBuilt a poll reloaded the network for.
  let rebuilt: string | null = null;
  let tripsById: ReadonlyMap<string, GtfsTrip> = new Map();
  // The last train response accepted, recomputed when the alerts or the
  // network change.
  type TrainResponse = { readonly data: GtfsTrains; readonly dataTime: number };
  let latest: TrainResponse | null = null;
  // A newly received response remains available if network promotion or an
  // alert update supersedes its preparation before its first publication.
  let current: TrainResponse | null = null;
  let gps = createGpsMemory();
  let alerts: Readonly<Record<LineId, LineAlert>> = {};
  let covered: ReadonlySet<LineId> = new Set();
  let pendingAlerts = { lines: alerts, covered };
  let application: AbortController | null = null;
  let applying: Promise<boolean> | null = null;
  let whole = !options.major;
  let loadingWhole: Promise<Network> | null = null;
  type PartitionFiles = { readonly network: GtfsNetworkFile; readonly geometry: GtfsGeometryFile | null };
  let partitionManifest: GtfsPartitionManifest | null = null;
  let manifestLoading: {
    readonly token: object;
    readonly request: Promise<GtfsPartitionManifest>;
  } | null = null;
  let partitionBounds: GtfsPartitionBox | null = null;
  let partitionDesiredDetailed = false;
  let selectedPartIds: readonly string[] = [];
  let selectedPartKey = '';
  let pendingPartIds: readonly string[] = [];
  let pendingPartKey = '';
  let partitionRepairBuilt: string | null = null;
  let partitionLoading: Promise<Network> | null = null;
  let partitionRepairLoading: Promise<Network> | null = null;
  let partitionAbort: AbortController | null = null;
  let partitionRepairAbort: AbortController | null = null;
  const partitionFiles = new Map<string, PartitionFiles>();
  const recoveredPartitionGeometry = new Map<string, GtfsGeometryFile>();
  const partitionLoads = new Map<string, { readonly token: object; readonly request: Promise<PartitionFiles> }>();
  const partitionGeometry = new Map<string, OptionalJSON<GtfsGeometryFile>>();

  function cancelApplication(): void {
    application?.abort();
    application = null;
    applying = null;
  }

  // A partition selection is a different live-data scope. Do not carry the
  // previous scope's response or its trains into the replacement network:
  // besides showing irrelevant vehicles, recomputing a national response
  // while city partitions prepare creates a large avoidable memory peak.
  function clearTrainScope(): void {
    const hadData =
      latest !== null ||
      current !== null ||
      tripsById.size > 0 ||
      store.groupTimes.has(key) ||
      store.groupUpdates.has(key);
    cancelApplication();
    const previous = current ?? latest;
    const empty = previous ? { data: { ...previous.data, trips: [], feeds: {} }, dataTime: 0 } : null;
    // Keep an empty accepted response so network promotion remains an atomic,
    // cooperative publication while retaining none of the old scope's trips.
    latest = empty;
    current = empty;
    tripsById = new Map();
    gps.clear();
    clearRegion(store, lines, [key]);
    if (hadData) options.onUpdate();
  }

  function invalidateNetworkGeneration(): number {
    const reason = new Error('Partition selection superseded');
    partitionAbort?.abort(reason);
    partitionAbort = null;
    partitionRepairAbort?.abort(reason);
    partitionRepairAbort = null;
    // Optional geometry retries are bound to that selection's abort signal.
    // Even a retained network part needs a fresh request in the next
    // selection rather than an inactive cached retry.
    for (const request of partitionGeometry.values()) request.cancel();
    partitionGeometry.clear();
    const generation = ++networkGeneration;
    if (pendingNetwork !== null) {
      pendingNetwork = null;
      cancelApplication();
    }
    return generation;
  }

  function publishNetwork(prepared: NetworkPublication): void {
    local = prepared.net;
    file = prepared.file;
    pendingNetwork = null;
    prepared.publish?.();
  }

  function* prepare(
    response: TrainResponse,
    net: Network,
    t: number,
    networkFile: GtfsNetworkFile | null,
    alertLines: Readonly<Record<LineId, LineAlert>>,
  ) {
    // Canceled conversion must not teach later responses rejected GPS fixes.
    const memory = createGpsMemory();
    for (const [id, fix] of gps) {
      memory.set(id, fix);
      yield;
    }
    const normal = yield* computeTrainsSteps(
      net,
      { ...response.data, trips: response.data.trips.filter((trip) => trip.unrouted !== true) },
      t,
      networkFile?.shapes ?? {},
      memory,
      closuresOf(net, alertLines),
    );
    const all = storeIds({
      ...normal,
      trains: [
        ...normal.trains,
        ...response.data.trips.flatMap((trip) => {
          const point = unroutedGps(trip, net, t);
          return point ? [point] : [];
        }),
      ],
    });
    // A server rebuild can add routes before this page has their styles.
    // Unknown lines would throw in the renderer and survive clearRegion.
    const trains = [];
    const byStation = new Map<string, readonly Arrival[]>();
    for (const train of all.trains) {
      if (knownLines.has(train.lineId)) trains.push(train);
      yield;
    }
    for (const [sid, arrivals] of all.byStation) {
      const shown = arrivals.filter((arrival) => knownLines.has(arrival.lineId));
      if (shown.length > 0) byStation.set(sid, shown);
      yield;
    }
    const trips = new Map<string, GtfsTrip>();
    for (const trip of response.data.trips) {
      trips.set(trip.id, trip);
      yield;
    }
    const computed: ComputedTrains = { ...all, trains, byStation };
    return { computed, memory, trips };
  }

  function applyLatest(): Promise<boolean> {
    const networkSnapshot = pendingNetwork;
    const net = networkSnapshot?.net ?? local;
    if (!current || !net || !loop.running()) return Promise.resolve(false);
    cancelApplication();
    const request = new AbortController();
    application = request;
    const response = current;
    const alertSnapshot = pendingAlerts;
    const accepted = response !== latest;
    const t = now();
    const result: Promise<boolean> = (async (): Promise<boolean> => {
      try {
        const prepared = await cooperate(
          prepare(response, net, t, networkSnapshot?.file ?? file, alertSnapshot.lines),
          request.signal,
        );
        const publish = await cooperate(
          prepareComputedSteps(store, key, lines, prepared.computed, response.dataTime, t),
          request.signal,
        );
        request.signal.throwIfAborted();
        if (
          current !== response ||
          pendingNetwork !== networkSnapshot ||
          (networkSnapshot === null && local !== net) ||
          pendingAlerts !== alertSnapshot ||
          !loop.running() ||
          (response.dataTime !== 0 && response.dataTime < (store.groupTimes.get(key) ?? 0))
        )
          return false;
        const received = accepted ? Date.now() : (store.groupUpdates.get(key) ?? Date.now());
        // The promoted network and its feed become visible in one task.
        // While preparing, frames still use the old network and old trains.
        if (networkSnapshot) publishNetwork(networkSnapshot);
        if (!publish(received)) return false;
        gps = prepared.memory;
        tripsById = prepared.trips;
        latest = response;
        current = latest;
        alerts = alertSnapshot.lines;
        covered = alertSnapshot.covered;
        if (accepted) {
          store.failedGroups = store.failedGroups.filter((k) => k !== key);
          options.onProblem(feedsState(response.data.feeds).down, response.data.feeds);
        }
        options.onUpdate();
        return true;
      } catch (error) {
        // start()/full() still wait for the response when a network or alert
        // update replaces its preparation. stop() removes the successor.
        if (request.signal.aborted) {
          if (application === request || !applying) return false;
          return await applying;
        }
        throw error;
      } finally {
        if (application === request) {
          application = null;
          applying = null;
        }
      }
    })();
    applying = result;
    return result;
  }

  async function install(
    data: GtfsNetworkFile,
    net: Network,
    geometry: GeometryFile | null,
    generation: number,
    signal?: AbortSignal,
  ): Promise<Network> {
    const mine = lifetime;
    if (data.unrouted) {
      const stations = new Map(net.stations);
      const gpsIds = new Set<string>();
      for (const patterns of Object.values(data.unrouted))
        for (const pattern of patterns) for (const id of pattern.ids) gpsIds.add(id);
      let east = -Infinity,
        north = -Infinity,
        south = Infinity,
        west = Infinity;
      for (const [id, row] of Object.entries(data.stations)) {
        if (!stations.has(id)) stations.set(id, { id, name: row[0], lat: row[1], lon: row[2], lines: new Set() });
        if (gpsIds.has(id)) {
          west = Math.min(west, row[2]);
          south = Math.min(south, row[1]);
          east = Math.max(east, row[2]);
          north = Math.max(north, row[1]);
        }
      }
      net = { ...net, stations, ...(Number.isFinite(west) ? { gpsBounds: [west, south, east, north] as const } : {}) };
    }
    const shaped = await cooperate(Core.attachGeometrySteps(net, geometry), signal);
    if (generation !== networkGeneration) {
      if (local) return local;
      throw new Error('City network released');
    }
    let prepared = shaped;
    // Loading the minor tier must not erase track already accepted for this
    // build while the larger geometry file is unavailable. Reuse only a
    // connection whose endpoints and adjacency still match the new network.
    if (local && file?.built === data.built && shaped.geom) {
      const retained = new Map(shaped.geom);
      for (const [segmentKey, shape] of local.geom ?? []) {
        if (retained.has(segmentKey)) continue;
        const [line, from, to] = segmentKey.split('|');
        if (
          line === undefined ||
          from === undefined ||
          to === undefined ||
          net.lines[line]?.adj.get(from)?.has(to) !== true
        )
          continue;
        const a = net.stations.get(from);
        const b = net.stations.get(to);
        const oldA = local.stations.get(from);
        const oldB = local.stations.get(to);
        if (
          a &&
          b &&
          oldA &&
          oldB &&
          a.lat === oldA.lat &&
          a.lon === oldA.lon &&
          b.lat === oldB.lat &&
          b.lon === oldB.lon
        )
          retained.set(segmentKey, shape);
      }
      prepared = { ...shaped, geom: retained };
    }
    const publish = await options.prepareNetwork?.(prepared, signal);
    if (generation !== networkGeneration) {
      if (local) return local;
      throw new Error('City network released');
    }
    const publication = { file: data, net: prepared, publish };
    pendingNetwork = publication;
    cancelApplication();
    await applyLatest();
    if (mine !== lifetime) throw new Error('City network released');
    if (generation !== networkGeneration) {
      if (local) return local;
      throw new Error('City network released');
    }
    // No train response yet, or polling stopped while preparation was in
    // flight: static loading still completes without publishing a stale feed.
    if (pendingNetwork === publication) publishNetwork(publication);
    return local ?? prepared;
  }

  const partitionUrl = (fileName: string, id: string, built: string): string => {
    const query = new URLSearchParams({ id, built }).toString();
    return `${base}${fileName}${version}${version === '' ? '?' : '&'}${query}`;
  };

  const geometryValue = (value: unknown): GtfsGeometryFile | null => {
    if (!options.isGeometry(value) || typeof value !== 'object' || value === null) return null;
    const source = 'source' in value && typeof value.source === 'string' ? value.source : '';
    const format = 'format' in value && typeof value.format === 'string' ? value.format : '';
    const segments: Record<string, Readonly<Record<string, string>>> = {};
    for (const [lineId, line] of Object.entries(value.segments ?? {})) {
      if (!line) continue;
      const selected: Record<string, string> = {};
      for (const [segment, shape] of Object.entries(line)) if (typeof shape === 'string') selected[segment] = shape;
      segments[lineId] = selected;
    }
    return { source, format, segments };
  };

  async function loadPartitionManifest(): Promise<GtfsPartitionManifest> {
    if (partitionManifest !== null) return partitionManifest;
    const active = manifestLoading;
    if (active !== null) return active.request;
    const mine = lifetime;
    const token = {};
    const request = (async () => {
      const value = await loadJSON(`${base}network-parts.json${version}`);
      if (mine !== lifetime || manifestLoading?.token !== token) throw new Error('City network released');
      if (!isPartitionManifest(value)) throw new TypeError(`${name}: network-parts.json is not a manifest`);
      if (new Set(value.parts.map((part) => part.id)).size !== value.parts.length)
        throw new TypeError(`${name}: network-parts.json has duplicate ids`);
      partitionManifest = value;
      return value;
    })();
    manifestLoading = { token, request };
    try {
      return await request;
    } finally {
      if (manifestLoading?.token === token) manifestLoading = null;
    }
  }

  function clearPartitionFiles(clearManifest: boolean, clearActive = true): void {
    for (const request of partitionGeometry.values()) request.cancel();
    partitionGeometry.clear();
    partitionFiles.clear();
    recoveredPartitionGeometry.clear();
    partitionLoads.clear();
    selectedPartIds = [];
    selectedPartKey = '';
    pendingPartIds = [];
    pendingPartKey = '';
    partitionRepairBuilt = null;
    partitionRepairLoading = null;
    if (clearActive) partitionLoading = null;
    if (clearManifest) {
      partitionManifest = null;
      if (clearActive) manifestLoading = null;
    }
  }

  async function installPartitionSelection(
    generation: number,
    ids: readonly string[],
    built: string,
    signal?: AbortSignal,
  ): Promise<Network> {
    const files = ids.flatMap((id) => {
      const found = partitionFiles.get(partKey(built, id));
      return found ? [found] : [];
    });
    if (files.length !== ids.length) throw new Error(`${name}: incomplete network partition selection`);
    const data = mergeNetworkParts(
      built,
      files.map((part) => part.network),
    );
    const net = await gtfsNetworkAsync(data, signal);
    const geometry = mergeGeometryParts(files.flatMap((part) => (part.geometry ? [part.geometry] : [])));
    return install(data, net, geometry, generation, signal);
  }

  function finishPartitionLoad(request: Readonly<Promise<Network>>): void {
    if (partitionLoading !== request) return;
    partitionLoading = null;
    const repairBuilt = partitionRepairBuilt;
    partitionRepairBuilt = null;
    if (repairBuilt !== null) repairPartitionSelection(repairBuilt);
  }

  function finishPartitionRepair(request: Readonly<Promise<Network>>): void {
    if (partitionRepairLoading !== request) return;
    partitionRepairLoading = null;
    partitionRepairAbort = null;
    const repairBuilt = partitionRepairBuilt;
    partitionRepairBuilt = null;
    if (repairBuilt !== null) repairPartitionSelection(repairBuilt);
  }

  function repairPartitionSelection(built: string): void {
    if (partitionLoading !== null || partitionRepairLoading !== null) {
      partitionRepairBuilt = built;
      return;
    }
    if (partitionManifest?.built !== built || file?.built !== built || selectedPartIds.length === 0) return;
    const generation = networkGeneration;
    const controller = new AbortController();
    partitionRepairAbort = controller;
    const repaired = installPartitionSelection(generation, selectedPartIds, built, controller.signal).then((net) => {
      if (generation === networkGeneration) options.onNetwork(net);
      return net;
    });
    partitionRepairLoading = repaired;
    void repaired.then(
      () => {
        finishPartitionRepair(repaired);
        return null;
      },
      () => {
        finishPartitionRepair(repaired);
        return null;
      },
    );
  }

  function recoveredGeometry(id: string, built: string, geometry: GtfsGeometryFile): void {
    const cacheKey = partKey(built, id);
    partitionGeometry.delete(cacheKey);
    const part = partitionFiles.get(cacheKey);
    if (!part || part.network.built !== built) {
      recoveredPartitionGeometry.set(cacheKey, geometry);
      return;
    }
    partitionFiles.set(cacheKey, { network: part.network, geometry });
    if (partitionManifest?.built !== built || (!selectedPartIds.includes(id) && !pendingPartIds.includes(id))) return;
    if (partitionLoading !== null) {
      partitionRepairBuilt = built;
      return;
    }
    repairPartitionSelection(built);
  }

  function replaceStalePartitionGeometry(id: string, built: string): void {
    if (partitionManifest?.built !== built || (!selectedPartIds.includes(id) && !pendingPartIds.includes(id))) return;
    const bounds = partitionBounds;
    if (bounds === null) return;
    const active = partitionLoading;
    const detailed = partitionDesiredDetailed;
    invalidateNetworkGeneration();
    clearPartitionFiles(true, active === null);
    if (active !== null) return;
    const replacement = loadPartitions(bounds, detailed);
    void replacement.then(
      (net) => {
        options.onNetwork(net);
        return null;
      },
      (error: unknown) => {
        console.warn(`${name}: partition generation reload failed`, error);
        return null;
      },
    );
  }

  function geometryRequest(id: string, built: string, signal?: AbortSignal): OptionalJSON<GtfsGeometryFile> {
    const cacheKey = partKey(built, id);
    let request = partitionGeometry.get(cacheKey);
    if (request) return request;
    const mine = lifetime;
    request = loadOptionalJSON(
      partitionUrl('geometry-part.json', id, built),
      (value): value is GtfsGeometryFile => geometryValue(value) !== null,
      (value) => {
        if (mine !== lifetime || partitionGeometry.get(cacheKey) !== request) return;
        const geometry = geometryValue(value);
        if (geometry) recoveredGeometry(id, built, geometry);
      },
      {
        stopsRetry: (error) => errorStatus(error) === 409,
        stoppedRetry: () => {
          if (mine === lifetime && partitionGeometry.get(cacheKey) === request)
            replaceStalePartitionGeometry(id, built);
        },
      },
      signal,
    );
    partitionGeometry.set(cacheKey, request);
    return request;
  }

  function loadPartition(id: string, built: string, signal?: AbortSignal): Promise<PartitionFiles> {
    const cacheKey = partKey(built, id);
    const cached = partitionFiles.get(cacheKey);
    if (cached) {
      if (cached.geometry === null) void geometryRequest(id, built, signal);
      return Promise.resolve(cached);
    }
    const active = partitionLoads.get(cacheKey);
    if (active) return active.request;
    const mine = lifetime;
    const token = {};
    const geometry = geometryRequest(id, built, signal);
    const request = (async () => {
      try {
        const [networkValue, geometryValueRaw] = await Promise.all([
          loadJSON(partitionUrl('network-part.json', id, built), signal),
          geometry.first,
        ]);
        if (mine !== lifetime || partitionLoads.get(cacheKey)?.token !== token)
          throw new Error('City network released');
        if (!isNetworkFile(networkValue) || networkValue.built !== built)
          throw new TypeError(`${name}: invalid network partition ${id}`);
        const foundGeometry = geometryValue(geometryValueRaw) ?? recoveredPartitionGeometry.get(cacheKey) ?? null;
        recoveredPartitionGeometry.delete(cacheKey);
        if (foundGeometry !== null && partitionGeometry.get(cacheKey) === geometry) partitionGeometry.delete(cacheKey);
        const found = { network: networkValue, geometry: foundGeometry };
        partitionFiles.set(cacheKey, found);
        return found;
      } catch (error) {
        geometry.cancel();
        if (partitionLoads.get(cacheKey)?.token === token) recoveredPartitionGeometry.delete(cacheKey);
        if (partitionGeometry.get(cacheKey) === geometry) partitionGeometry.delete(cacheKey);
        throw error;
      } finally {
        if (partitionLoads.get(cacheKey)?.token === token) partitionLoads.delete(cacheKey);
      }
    })();
    partitionLoads.set(cacheKey, { token, request });
    return request;
  }

  async function selectPartitionNetwork(bounds: GtfsPartitionBox, retry = true): Promise<Network> {
    const mine = lifetime;
    const session = partitionSession;
    const stopped = (): Network => {
      if (local !== null) return local;
      throw new Error(`${name}: partition selection stopped`);
    };
    const manifest = await loadPartitionManifest();
    if (mine !== lifetime) throw new Error('City network released');
    if (session !== partitionSession) return stopped();
    const currentBounds = partitionBounds ?? bounds;
    const includeMinor = partitionDesiredDetailed;
    const selection = partitionSelection(manifest, currentBounds, includeMinor);
    pendingPartIds = selection.ids;
    pendingPartKey = selection.key;
    if (selection.key === selectedPartKey && local !== null) return local;
    const generation = invalidateNetworkGeneration();
    const controller = new AbortController();
    partitionAbort = controller;
    const { signal } = controller;
    if (selection.all) {
      // The combined files supersede every decoded subset. Do not retain a
      // previous city selection beside the country-sized response.
      for (const request of partitionGeometry.values()) request.cancel();
      partitionGeometry.clear();
      partitionFiles.clear();
      partitionLoads.clear();
      recoveredPartitionGeometry.clear();
    } else {
      geometryLoad?.cancel();
      geometryLoad = null;
    }
    try {
      let result: Network;
      if (selection.all) result = await fetchFilesAtGeneration('', generation, signal);
      else {
        await Promise.all(selection.ids.map((id) => loadPartition(id, manifest.built, signal)));
        if (mine !== lifetime) throw new Error('City network released');
        if (generation !== networkGeneration)
          return session === partitionSession
            ? await selectPartitionNetwork(partitionBounds ?? currentBounds, retry)
            : stopped();
        result = await installPartitionSelection(generation, selection.ids, manifest.built, signal);
      }
      if (mine !== lifetime) throw new Error('City network released');
      if (generation !== networkGeneration)
        return session === partitionSession
          ? await selectPartitionNetwork(partitionBounds ?? currentBounds, retry)
          : stopped();
      selectedPartIds = selection.ids;
      selectedPartKey = selection.key;
      for (const [cacheKey] of partitionFiles)
        if (!selection.ids.includes(partId(cacheKey)) || !cacheKey.startsWith(`${manifest.built}|`))
          partitionFiles.delete(cacheKey);
      for (const [cacheKey, request] of partitionGeometry) {
        if (selection.ids.includes(partId(cacheKey)) && cacheKey.startsWith(`${manifest.built}|`)) continue;
        request.cancel();
        partitionGeometry.delete(cacheKey);
      }
      return result;
    } catch (error) {
      if (mine !== lifetime) throw error;
      if (generation !== networkGeneration)
        return session === partitionSession
          ? selectPartitionNetwork(partitionBounds ?? currentBounds, retry)
          : stopped();
      if (retry && errorStatus(error) === 409) {
        clearPartitionFiles(true, false);
        return selectPartitionNetwork(partitionBounds ?? bounds, false);
      }
      throw error;
    }
  }

  function loadPartitions(bounds: GtfsPartitionBox, includeMinor: boolean): Promise<Network> {
    partitionBounds = bounds;
    partitionDesiredDetailed = includeMinor;
    if (partitionLoading !== null) return partitionLoading;
    const request = selectPartitionNetwork(bounds);
    partitionLoading = request;
    void request.then(
      () => {
        finishPartitionLoad(request);
        return null;
      },
      () => {
        finishPartitionLoad(request);
        return null;
      },
    );
    return request;
  }

  // The network and, alongside, its geometry: optional, and retried until
  // it comes (the straight-line network meanwhile).
  async function fetchFilesAtGeneration(
    suffix: '' | '-major',
    generation: number,
    signal?: AbortSignal,
  ): Promise<Network> {
    const mine = lifetime;
    // Keep applying the received feed until install has a replacement ready.
    // A pending or failed download must not discard its current preparation.
    geometryLoad?.cancel();
    let installed: { readonly data: GtfsNetworkFile; readonly net: Network } | null = null;
    const late: { geometry: GeometryFile | null } = { geometry: null };
    const geometryFile = loadOptionalJSON(
      `${base}geometry${suffix}.json${version}`,
      options.isGeometry,
      (geometry) => {
        if (generation !== networkGeneration) return;
        late.geometry = geometry;
        if (installed) {
          void install(installed.data, installed.net, geometry, generation, signal)
            .then((net) => {
              if (generation === networkGeneration) options.onNetwork(net);
              return null;
            })
            .catch((error: unknown) => {
              if (generation === networkGeneration) console.warn(name + ': geometry installation failed', error);
            });
        }
      },
      undefined,
      signal,
    );
    geometryLoad = geometryFile;
    try {
      const data = await loadJSON(`${base}network${suffix}.json${version}`, signal);
      if (mine !== lifetime) throw new Error('City network released');
      if (!isNetworkFile(data)) throw new TypeError(`${name}: network${suffix}.json is not a network`);
      // The whole network arrived first: the trimmed one is out of date.
      if (suffix !== '' && whole && local) {
        geometryFile.cancel();
        return local;
      }
      const net = await gtfsNetworkAsync(data, signal);
      const geometry = (await geometryFile.first) ?? late.geometry;
      if (mine !== lifetime) throw new Error('City network released');
      if (generation !== networkGeneration && local) return local;
      let result = await install(data, net, geometry, generation, signal);
      // A geometry retry may finish while the initial shapes are prepared.
      if (late.geometry && late.geometry !== geometry)
        result = await install(data, net, late.geometry, generation, signal);
      if (mine !== lifetime) throw new Error('City network released');
      installed = { data, net };
      if (geometry !== null && geometryLoad === geometryFile) geometryLoad = null;
      return result;
    } catch (error) {
      geometryFile.cancel();
      throw error;
    }
  }

  function fetchFiles(suffix: '' | '-major'): Promise<Network> {
    return fetchFilesAtGeneration(suffix, invalidateNetworkGeneration());
  }

  async function fetchNetwork(): Promise<Network> {
    const mine = lifetime;
    if (!whole) {
      try {
        return await fetchFiles('-major');
      } catch (error) {
        if (mine !== lifetime) throw error;
        // A server without the trimmed files (one with no rebuilds serves
        // only public/'s): the whole network.
        console.warn(`${name}: no network without its minor lines`, error);
        whole = true;
      }
    }
    return fetchFiles('');
  }

  function view(bounds: GtfsPartitionBox, includeMinor: boolean): boolean {
    if (options.partitioned !== true) return false;
    partitionBounds = bounds;
    partitionDesiredDetailed = includeMinor;
    if (partitionManifest === null) return local !== null;
    const selection = partitionSelection(partitionManifest, bounds, includeMinor);
    const currentKey = partitionLoading === null ? selectedPartKey : pendingPartKey;
    if (selection.key === currentKey) return false;
    if (selectedPartKey !== '') clearTrainScope();
    if (partitionLoading !== null) {
      invalidateNetworkGeneration();
      pendingPartIds = selection.ids;
      pendingPartKey = selection.key;
    }
    loop.replace();
    return true;
  }

  function load(bounds?: GtfsPartitionBox): Promise<Network> {
    if (options.partitioned === true) {
      const viewBounds = bounds ?? partitionBounds;
      if (viewBounds === null) return Promise.reject(new Error(`${name}: partitioned network requires map bounds`));
      const includeMinor = partitionDesiredDetailed;
      if (partitionManifest !== null) {
        const selection = partitionSelection(partitionManifest, viewBounds, includeMinor);
        if (selection.key === selectedPartKey && local !== null) return Promise.resolve(local);
      }
      return loadPartitions(viewBounds, includeMinor);
    }
    if (!loading) {
      const request = fetchNetwork().catch((error: unknown) => {
        if (loading === request) loading = null;
        throw error;
      });
      loading = request;
    }
    return loading;
  }

  function full(bounds?: GtfsPartitionBox): Promise<Network> | null {
    if (options.partitioned === true) {
      const viewBounds = bounds ?? partitionBounds;
      if (viewBounds === null) return Promise.reject(new Error(`${name}: partitioned network requires map bounds`));
      partitionBounds = viewBounds;
      partitionDesiredDetailed = true;
      if (partitionManifest !== null) {
        const selection = partitionSelection(partitionManifest, viewBounds, true);
        if (selection.key === selectedPartKey && local !== null) return null;
      }
      return loadPartitions(viewBounds, true);
    }
    if (whole) return null;
    const mine = lifetime;
    loadingWhole ??= fetchFiles('').then(
      (net) => {
        if (mine !== lifetime) throw new Error('City network released');
        whole = true;
        return net;
      },
      (error: unknown) => {
        if (mine === lifetime) loadingWhole = null;
        throw error;
      },
    );
    return loadingWhole;
  }

  const trainUrl = (selection: PartitionSelection): string => {
    if (selection.all) return `${base}trains`;
    const query = new URLSearchParams({ built: selection.built });
    for (const id of selection.ids) query.append('id', id);
    return `${base}trains?${query.toString()}`;
  };

  async function sampledTrains(signal: AbortSignal): Promise<{
    readonly sample: SampledJSON;
    readonly selectionKey: string | null;
  }> {
    if (options.partitioned !== true) {
      return { sample: await api.getSampledJSON(`${base}trains`, signal), selectionKey: null };
    }
    const bounds = partitionBounds;
    if (bounds === null) throw new Error(`${name}: partitioned trains require map bounds`);
    const manifest = await loadPartitionManifest();
    const selection = partitionSelection(manifest, partitionBounds ?? bounds, partitionDesiredDetailed);
    return { sample: await api.getSampledJSON(trainUrl(selection), signal), selectionKey: selection.key };
  }

  function prefetch(bounds: GtfsPartitionBox, includeMinor: boolean): void {
    if (options.partitioned !== true) {
      void api.getSampledJSON(`${base}trains`).catch(() => null);
      return;
    }
    partitionBounds = bounds;
    partitionDesiredDetailed = includeMinor;
    void loadPartitionManifest()
      .then((manifest) => api.getSampledJSON(trainUrl(partitionSelection(manifest, bounds, includeMinor))))
      .catch(() => null);
  }

  const hintDelay = createPollHintDelay(options.now);
  const loop = pollLoop(
    async (signal) => {
      let answer: { readonly sample: SampledJSON; readonly selectionKey: string | null };
      try {
        // Wake the feed while static files download, including on a cold visit.
        const result = await Promise.all([
          options.partitioned === true ? load() : local ? Promise.resolve(local) : load(),
          sampledTrains(signal),
        ]);
        answer = result[1];
      } catch (error) {
        if (options.partitioned !== true || errorStatus(error) !== 409) throw error;
        const bounds = partitionBounds;
        if (bounds === null) throw error;
        const active = partitionLoading;
        invalidateNetworkGeneration();
        clearPartitionFiles(true, active === null);
        const net = await loadPartitions(bounds, partitionDesiredDetailed);
        if (signal.aborted) return false;
        options.onNetwork(net);
        loop.refresh();
        return true;
      }
      if (signal.aborted) return false;
      const { data, dataTime } = answer.sample;
      if (answer.selectionKey !== null) {
        const manifest = partitionManifest;
        const bounds = partitionBounds;
        if (
          manifest === null ||
          bounds === null ||
          partitionSelection(manifest, bounds, partitionDesiredDetailed).key !== answer.selectionKey
        )
          return true;
      }
      if (!isTrains(data)) throw new TypeError(`${name}: invalid train data`);
      const newestTime = Math.max(store.groupTimes.get(key) ?? 0, current?.dataTime ?? 0);
      const stale = dataTime !== 0 && dataTime < newestTime;
      if (stale) return feedsState(current?.data.feeds ?? data.feeds).loading;
      // The server rebuilt its timetable: its trips may call at stops the
      // loaded network hasn't. Its network again, once per build (a host
      // still serving the old file isn't asked again every poll).
      if (data.networkBuilt !== undefined && data.networkBuilt !== file?.built && data.networkBuilt !== rebuilt) {
        if (options.onRebuilt) {
          options.onRebuilt();
          return false;
        }
        let net: Network;
        if (options.partitioned === true) {
          const bounds = partitionBounds;
          if (bounds === null) throw new Error(`${name}: rebuilt partitioned network has no map bounds`);
          const detailed = partitionDesiredDetailed;
          const active = partitionLoading;
          invalidateNetworkGeneration();
          clearPartitionFiles(true, active === null);
          net = await loadPartitions(bounds, detailed);
        } else {
          loading = null;
          net = await load();
        }
        if (signal.aborted) return false;
        rebuilt = data.networkBuilt;
        // Installed either way: the page draws the network this client has.
        options.onNetwork(net);
        if (signal.aborted) return false;
      }
      cancelApplication();
      current = { data, dataTime };
      await applyLatest();
      if (signal.aborted) return false;
      const feeds = feedsState(data.feeds);
      return feeds.loading;
    },
    options.pollMs,
    (error) => {
      console.warn(`${name}: train refresh failed`, error);
      if (!store.failedGroups.includes(key)) store.failedGroups.push(key);
      options.onProblem(null);
    },
    options.onUpdate,
    () => {
      const data = current?.data;
      const next = data && 'nextPollAt' in data ? data.nextPollAt : undefined;
      return hintDelay(next);
    },
  );

  const alertLoop = pollLoop(
    async (signal) => {
      const data = await api.getJSON(`${base}alerts`, signal);
      if (signal.aborted) return false;
      if (!isAlerts(data)) throw new TypeError(`${name}: invalid alerts`);
      pendingAlerts = { lines: data.lines, covered: new Set(data.covered ?? []) };
      // A closure hides scheduled trains now, not at the next poll.
      if (local && current) await applyLatest();
      else {
        alerts = pendingAlerts.lines;
        covered = pendingAlerts.covered;
        options.onUpdate();
      }
      return false;
    },
    ALERTS_MS,
    // Keeps the last alerts, but no longer claims a good service it can't
    // vouch for.
    (error) => {
      console.warn(`${name}: alerts unavailable`, error);
      if (covered.size > 0) {
        covered = new Set();
        pendingAlerts.covered = covered;
        options.onUpdate();
      }
    },
  );

  function stop(): void {
    loop.stop();
    hintDelay();
    alertLoop.stop();
    cancelApplication();
    if (options.partitioned === true) {
      partitionSession++;
      invalidateNetworkGeneration();
      geometryLoad?.cancel();
      geometryLoad = null;
      partitionLoading = null;
      // An active geometry repair may settle after stop. Detach it and drop
      // any queued successor so neither can restart against the new session.
      partitionRepairBuilt = null;
      partitionRepairLoading = null;
    }
    current = latest;
    pendingAlerts = { lines: alerts, covered };
  }
  function clear(): void {
    cancelApplication();
    latest = null;
    current = null;
    tripsById = new Map();
    gps.clear();
    alerts = {};
    covered = new Set();
    pendingAlerts = { lines: alerts, covered };
    clearRegion(store, lines, [key]);
  }

  return {
    load,
    full,
    view,
    network: () => local,
    file: () => file,
    prefetch,
    start() {
      if (options.alerts) void alertLoop.start();
      return loop.start();
    },
    stop,
    clear,
    release() {
      stop();
      clear();
      lifetime++;
      invalidateNetworkGeneration();
      geometryLoad?.cancel();
      geometryLoad = null;
      local = null;
      pendingNetwork = null;
      file = null;
      loading = null;
      loadingWhole = null;
      whole = !options.major;
      clearPartitionFiles(true);
      partitionBounds = null;
      partitionDesiredDetailed = false;
      rebuilt = null;
    },
    polling: loop.running,
    loading: loop.loading,
    trip: (id) => tripsById.get(id),
    alert: (line) => alerts[line],
    covered: (line) => covered.has(line),
  };
}
